import { useRef, useState } from "react";
import { Eye, ChevronDown, ChevronLeft, ChevronRight, Wallet } from "lucide-react";
import { CLP } from "@/lib/format";

const calc = (precio, fees) => {
  const vivo = fees.commission_clp;
  const flow = Math.round(precio * (fees.flow_fee_pct || 0.025));
  return { precio, vivo, flow, neto: precio - vivo - flow };
};

const Column = ({ title, badge, d, fees, flowPct, id }) => (
  <div className={`rounded-lg border p-3 space-y-1.5 ${badge ? "border-emerald-500/50 bg-emerald-50/60 dark:bg-emerald-950/20" : "border-border bg-background"}`} data-testid={`breakdown-col-${id}`}>
    <div className="flex items-center justify-between gap-2">
      <p className="text-xs font-semibold uppercase tracking-wide">{title}</p>
      {badge && <span className="text-[10px] font-bold rounded-full bg-emerald-600 text-white px-2 py-0.5" data-testid="offer-badge">{badge}</span>}
    </div>
    <div className="flex justify-between"><span className="text-muted-foreground">Cobro al alumno</span><span data-testid={`sim-gross-${id}`}>{CLP(d.precio)}</span></div>
    <div className="flex justify-between gap-2 text-destructive"><span>(−) Tarifa VIVOTaller ({fees.commission_uf} UF)</span><span className="whitespace-nowrap" data-testid={`sim-vivo-${id}`}>- {CLP(d.vivo)}</span></div>
    <div className="flex justify-between gap-2 text-destructive"><span>(−) Pasarela de pago (~{flowPct}%)</span><span className="whitespace-nowrap" data-testid={`sim-flow-${id}`}>- {CLP(d.flow)}</span></div>
    <div className="flex justify-between gap-2 font-bold border-t border-border pt-2">
      <span className="flex items-center gap-1"><Wallet className="w-4 h-4 text-primary" />Tu ganancia neta</span>
      <span className="whitespace-nowrap" data-testid={`sim-net-${id}`}>{CLP(d.neto)}</span>
    </div>
  </div>
);

const Slider = ({ slides }) => {
  const ref = useRef(null);
  const drag = useRef(null);
  const [idx, setIdx] = useState(0);
  const [dragging, setDragging] = useState(false);
  const pitch = () => { const k = ref.current?.children; return k && k.length > 1 ? k[1].offsetLeft - k[0].offsetLeft : 1; };
  const go = (i) => { const n = Math.max(0, Math.min(slides.length - 1, i)); ref.current?.scrollTo({ left: n * pitch(), behavior: "smooth" }); setIdx(n); };
  const onScroll = () => { if (!drag.current) setIdx(Math.round(ref.current.scrollLeft / pitch())); };
  const down = (e) => {
    if (e.pointerType !== "mouse") return;
    drag.current = { x: e.clientX, left: ref.current.scrollLeft }; setDragging(true);
    ref.current.setPointerCapture(e.pointerId);
  };
  const move = (e) => { if (drag.current) ref.current.scrollLeft = drag.current.left - (e.clientX - drag.current.x); };
  const up = () => {
    if (!drag.current) return;
    const delta = ref.current.scrollLeft - drag.current.left;
    drag.current = null; setDragging(false);
    go(Math.abs(delta) > 40 ? idx + Math.sign(delta) : idx);
  };
  return (
    <div data-testid="breakdown-slider">
      <div className="flex gap-1 p-1 rounded-full bg-muted mb-2" role="tablist">
        {slides.map((s, i) => (
          <button key={s.id} type="button" role="tab" aria-selected={idx === i} onClick={() => go(i)} data-testid={`breakdown-tab-${s.id}`}
            className={`flex-1 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${idx === i ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}>
            {s.tab}
          </button>
        ))}
      </div>
      <div className="relative">
        <div ref={ref} onScroll={onScroll} onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up}
          className={`flex gap-2 overflow-x-auto pr-8 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden select-none ${dragging ? "cursor-grabbing" : "cursor-grab snap-x snap-mandatory"}`}>
          {slides.map((s) => <div key={s.id} className="snap-start shrink-0 w-[88%]">{s.node}</div>)}
        </div>
        {idx > 0 && (
          <button type="button" onClick={() => go(idx - 1)} aria-label="Anterior" data-testid="breakdown-prev"
            className="absolute left-1 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full bg-background/90 border shadow flex items-center justify-center hover:bg-primary hover:text-primary-foreground transition-colors"><ChevronLeft className="w-4 h-4" /></button>
        )}
        {idx < slides.length - 1 && (
          <button type="button" onClick={() => go(idx + 1)} aria-label="Siguiente" data-testid="breakdown-next"
            className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full bg-background/90 border shadow flex items-center justify-center hover:bg-primary hover:text-primary-foreground transition-colors"><ChevronRight className="w-4 h-4" /></button>
        )}
      </div>
      <div className="flex items-center justify-center gap-1.5 mt-2">
        {slides.map((s, i) => (
          <button key={s.id} type="button" onClick={() => go(i)} aria-label={s.tab} data-testid={`breakdown-dot-${s.id}`}
            className={`h-2 rounded-full transition-[width,background-color] ${idx === i ? "w-5 bg-primary" : "w-2 bg-muted-foreground/30"}`} />
        ))}
        <span className="ml-2 text-[11px] text-muted-foreground">Desliza para comparar</span>
      </div>
    </div>
  );
};

export const IncomeBreakdown = ({ fees, balance }) => {
  if (!fees) return <div className="py-8 grid place-items-center text-sm text-muted-foreground">Cargando tarifas…</div>;
  return (
    <div className="space-y-3 text-sm" data-testid="income-breakdown">
      <div className="rounded-xl border border-border p-4 space-y-1.5">
        <p className="font-semibold">Publicar este taller</p>
        <p className="text-lg font-bold text-primary" data-testid="publication-fee-amount">Costo: 1 Ficha</p>
        {balance !== undefined && <p className={`text-xs font-medium ${balance > 0 ? "text-emerald-700" : "text-amber-700"}`} data-testid="publication-balance">Tienes {balance} ficha{balance === 1 ? "" : "s"} disponible{balance === 1 ? "" : "s"}.{balance === 0 && " Compra un paquete en Billetera o guarda el taller sin publicar."}</p>}
      </div>
      <p className="text-xs text-muted-foreground" data-testid="income-breakdown-note">1 ficha equivale a {String(fees.publication_fee_uf).replace(".", ",")} UF + IVA (≈ {CLP(fees.publication_fee_total_clp)} IVA incluido). Los alumnos reservan gratis y te pagan directamente a ti.</p>
    </div>
  );
};

export const CostBreakdown = ({ fees, price, offerPct }) => {
  const [open, setOpen] = useState(false);
  if (!fees) return null;
  const precio = Number(price) || 0;
  const pct = Number(offerPct) || 0;
  const flowPct = ((fees.flow_fee_pct || 0.025) * 100).toLocaleString("es-CL", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const normal = calc(precio, fees);
  const offer = pct > 0 ? calc(Math.round(precio * (1 - pct / 100)), fees) : null;
  return (
    <div className="rounded-xl border border-primary/20 bg-primary/5 text-sm mt-1" data-testid="cost-breakdown">
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} data-testid="cost-breakdown-toggle"
        className="w-full flex items-center justify-between gap-2 p-3 text-left font-medium text-primary hover:bg-primary/10 rounded-xl transition-colors">
        <span className="flex items-center gap-2"><Eye className="w-4 h-4 shrink-0" />Ver desglose de comisiones y ganancias estimadas por alumno</span>
        <ChevronDown className={`w-4 h-4 shrink-0 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      <div className={`grid transition-[grid-template-rows,opacity] duration-300 ease-out ${open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}>
        <div className="overflow-hidden" aria-hidden={!open} inert={open ? undefined : ""}>
          <div className="px-3 pb-3 space-y-3" data-testid="cost-breakdown-content">
            <div className="rounded-lg border border-border bg-background p-3 space-y-1">
              <div className="flex items-center justify-between font-semibold">
                <span>Costo de publicación <span className="font-normal text-muted-foreground">(único, por taller)</span></span>
                <span data-testid="publication-fee-amount">{fees.publication_fee_uf} UF ≈ {CLP(fees.publication_fee_clp)}</span>
              </div>
              <p className="text-xs text-muted-foreground">Es el mismo valor con o sin descuento promocional.</p>
            </div>
            {precio > 0 ? (offer ? (
              <Slider slides={[
                { id: "offer", tab: `Con oferta -${pct}%`, node: <Column title={`Con oferta (${CLP(offer.precio)})`} badge={`-${pct}%`} d={offer} fees={fees} flowPct={flowPct} id="offer" /> },
                { id: "normal", tab: "Precio normal", node: <Column title={`Precio normal (${CLP(normal.precio)})`} d={normal} fees={fees} flowPct={flowPct} id="normal" /> },
              ]} />
            ) : (
              <Column title="Desglose por alumno inscrito" d={normal} fees={fees} flowPct={flowPct} id="normal" />
            )) : <p className="text-xs text-muted-foreground">Ingresa un precio para ver el desglose por alumno.</p>}
            <p className="text-xs text-muted-foreground">La tarifa VIVOTaller y la pasarela se descuentan en tu liquidación. El alumno siempre paga solo el precio publicado.</p>
          </div>
        </div>
      </div>
    </div>
  );
};
